import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/utils/classnames';
import { MaterialIcon } from './MaterialIcon';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastInput {
  message: ReactNode;
  variant?: ToastVariant;
  /** Auto-dismiss timeout in ms; default 3000. Pass 0 to disable. */
  durationMs?: number;
}

interface ToastRecord extends ToastInput {
  id: number;
}

interface ToastContextValue {
  push: (toast: ToastInput) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLES: Record<ToastVariant, { bg: string; text: string; icon: string }> = {
  success: { bg: 'bg-[#E3FCEF]', text: 'text-[#006644]', icon: 'check_circle' },
  error: { bg: 'bg-error-container', text: 'text-on-error-container', icon: 'error' },
  info: { bg: 'bg-surface-container-high', text: 'text-primary', icon: 'info' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timersRef.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (toast: ToastInput) => {
      idRef.current += 1;
      const id = idRef.current;
      setToasts((prev) => [...prev, { id, variant: 'info', durationMs: 3000, ...toast }]);
      const duration = toast.durationMs ?? 3000;
      if (duration > 0) {
        const handle = window.setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, handle);
      }
    },
    [dismiss],
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((handle) => window.clearTimeout(handle));
      timersRef.current.clear();
    },
    [],
  );

  const value = useMemo<ToastContextValue>(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed left-1/2 top-stack-md z-[60] flex -translate-x-1/2 flex-col items-center gap-stack-sm">
        <AnimatePresence initial={false}>
          {toasts.map((toast) => {
            const style = VARIANT_STYLES[toast.variant ?? 'info'];
            return (
              <motion.div
                key={toast.id}
                layout
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                className={cn(
                  'pointer-events-auto inline-flex items-center gap-stack-sm rounded-lg px-stack-md py-stack-sm shadow-md',
                  'font-body-md text-body-md',
                  style.bg,
                  style.text,
                )}
                role="status"
              >
                <MaterialIcon name={style.icon} size={18} />
                <span>{toast.message}</span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be called inside <ToastProvider>');
  return ctx;
}
